import { iLayerZeroEndpointAbi } from 'abis/LayerZeroAbi'
import { wagmiConfig } from 'services/wallet/chains'
import { Address, Hex, PublicClient, zeroAddress } from 'viem'
import {
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  bscTestnet,
  mainnet,
  optimism,
  optimismSepolia,
  polygon,
  sepolia,
} from 'viem/chains'
import { ChainId } from '../interfaces'

type EstimateFeesParams = {
  client: PublicClient
  dstChainId: ChainId
  uaContractAddress: Address
  payload: Hex
  options: Hex
}

export const lzLookup: {
  [chain: number]: {
    eid: number
    lzEndpoint: Address
    sgComposer: Address
    sgETH: Address
  }
} = {
  //MAINNETS

  [arbitrum.id]: {
    eid: 110,
    lzEndpoint: '0x3c2269811836af69497E5F486A85D7316753cf62',
    sgComposer: '0xeCc19E177d24551aA7ed6Bc6FE566eCa726CC8a9',
    sgETH: '0x82CbeCF39bEe528B5476FE6d1550af59a9dB6Fc0',
  },
  [avalanche.id]: {
    eid: 106,
    lzEndpoint: '0x3c2269811836af69497E5F486A85D7316753cf62',
    sgComposer: '0xeCc19E177d24551aA7ed6Bc6FE566eCa726CC8a9',
    sgETH: zeroAddress,
  },
  [mainnet.id]: {
    eid: 101,
    lzEndpoint: '0x66A71Dcef29A0fFBDBE3c6a460a3B5BC225Cd675',
    sgComposer: '0xeCc19E177d24551aA7ed6Bc6FE566eCa726CC8a9',
    sgETH: '0x72E2F4830b9E45d52F80aC08CB2bEC0FeF72eD9c',
  },
  [optimism.id]: {
    eid: 111,
    lzEndpoint: '0x3c2269811836af69497E5F486A85D7316753cf62',
    sgComposer: '0xeCc19E177d24551aA7ed6Bc6FE566eCa726CC8a9',
    sgETH: '0xb69c8CBCD90A39D8D3d3ccf0a3E968511C3856A0',
  },
  [polygon.id]: {
    eid: 109,
    lzEndpoint: '0x3c2269811836af69497E5F486A85D7316753cf62',
    sgComposer: '0xeCc19E177d24551aA7ed6Bc6FE566eCa726CC8a9',
    sgETH: zeroAddress,
  },

  //TESTNETS

  [avalancheFuji.id]: {
    eid: 10106,
    lzEndpoint: '0x93f54D755A063cE7bB9e6Ac47Eccc8e33411d706',
    sgComposer: '0xd2b97823982380A980d686Ed7B7c3285875386ed',
    sgETH: zeroAddress,
  },
  [bscTestnet.id]: {
    eid: 10102,
    lzEndpoint: '0x6Fcb97553D41516Cb228ac03FdC8B9a0a9df04A1',
    sgComposer: '0x75D573607f5047C728D3a786BE3Ba33765712875',
    sgETH: zeroAddress,
  },
  [sepolia.id]: {
    eid: 10161,
    lzEndpoint: '0xae92d5aD7583AD66E49A0c67BAd18F6ba52dDDc1',
    sgComposer: '0x4febD509277f485A5feB90fb20DC0D3FAe6Bf856',
    sgETH: '0x5859b9d0Af9D84F3932efbF798E48014B59A3fbF',
  },
  [arbitrumSepolia.id]: {
    eid: 10231,
    lzEndpoint: '0x6098e96a28E02f27B1e6BD381f870F1C8Bd169d3',
    sgComposer: '0xb2d85b2484c910A6953D28De5D3B8d204f7DDf15',
    sgETH: '0x860a86E5bc19a6BE6989c9c179e27C731c7CF2A9',
  },
  [optimismSepolia.id]: {
    eid: 10232,
    lzEndpoint: '0x55370E0fBB5f5b8dAeD978BA1c075a499eB107B8',
    sgComposer: '0x285304CB4FeFCCfb8a59F4cb583987643FAA3791',
    sgETH: '0x8cAd17ec95529395D36EFa6918319af50A2C3d8f',
  },
}

export const LZ_HELPER: Record<number, Address> = {
  [arbitrum.id]: '0x58fCa961Cac3543299A8583079B92144615ff28D',
  [avalanche.id]: zeroAddress,
  [mainnet.id]: zeroAddress,
  [optimism.id]: zeroAddress,
  [polygon.id]: zeroAddress,
}

export const reverseLzLookup = (eid: number): ChainId => {
  const reverse = {
    //MAINNETS
    [lzLookup[arbitrum.id].eid]: arbitrum.id,
    [lzLookup[avalanche.id].eid]: avalanche.id,
    [lzLookup[mainnet.id].eid]: mainnet.id,
    [lzLookup[optimism.id].eid]: optimism.id,
    [lzLookup[polygon.id].eid]: polygon.id,
  }

  return reverse[eid as keyof typeof reverse]
}

export const estimateLzFees = async ({
  //here
  client,
  dstChainId,
  uaContractAddress,
  payload,
  options,
}: EstimateFeesParams) => {
  return await client.readContract({
    address: lzLookup[wagmiConfig.state.chainId].lzEndpoint,
    abi: iLayerZeroEndpointAbi,
    functionName: 'estimateFees',
    args: [lzLookup[dstChainId].eid, uaContractAddress, payload, false, options],
  })
}
